import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit2, Trash2, Key, Shield, Activity, 
  Monitor, Smartphone, Tablet, Globe, Clock, X, Check,
  Search, Filter, RefreshCw, Eye, EyeOff, UserCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available panels/pages for permissions
const AVAILABLE_PANELS = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main dashboard with targets' },
  { key: 'stock', label: 'Merged Stock', description: 'Combined stock from all locations' },
  { key: 'orders', label: 'Orders', description: 'Order management' },
  { key: 'todo', label: 'To-Do', description: 'Task management' },
  { key: 'pricelist', label: 'Price List', description: 'Price list & catalog' },
  { key: 'accounts', label: 'Accounts', description: 'Bank statements & transactions' },
  { key: 'projects', label: 'Projects', description: 'Project management' },
  { key: 'analytics', label: 'Analytics', description: 'Inventory alerts & sales analytics' },
  { key: 'product_planning', label: 'Product Planning', description: 'Product planning panel' },
  { key: 'messaging', label: 'Messaging', description: 'WhatsApp / SMS / Email center' },
  { key: 'media', label: 'Media Library', description: 'Product images and visual assets' },
  { key: 'documents', label: 'Documents', description: 'Company documents (COA, contracts, reports)' },
  { key: 'user_management', label: 'User Management', description: 'Manage users (Admin only)' }
];

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('users'); // 'users' or 'logs'
  
  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [userForm, setUserForm] = useState({
    username: '',
    password: '',
    role: 'user',
    is_active: true,
    permissions: {
      dashboard: true,
      stock: true,
      orders: true,
      todo: true,
      pricelist: true,
      accounts: true,
      projects: true,
      analytics: true,
      product_planning: false,
      messaging: false,
      media: false,
      documents: false,
      user_management: false
    }
  });
  
  // Logs filter
  const [logsFilter, setLogsFilter] = useState({
    username: '',
    searchTerm: ''
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (logsFilter.username) params.append('username', logsFilter.username);
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/users/activity-logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setActivityLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch activity logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchActivityLogs();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, userForm, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('User created successfully');
      setShowUserForm(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        username: userForm.username,
        role: userForm.role,
        permissions: userForm.permissions,
        is_active: userForm.is_active
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('User updated successfully');
      setEditingUser(null);
      setShowUserForm(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleImpersonate = async (userId, username) => {
    if (!window.confirm(`Preview the app as "${username}"?\n\nYou'll see exactly what they see. An orange banner will let you exit at any time.`)) return;
    try {
      const res = await axios.post(`${API}/users/${userId}/impersonate`, {}, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      const { access_token, user: targetUser } = res.data;
      if (typeof window.__startImpersonation === 'function') {
        window.__startImpersonation({
          ...targetUser,
          token: access_token,
        });
      } else {
        toast.error('Impersonation not available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start preview');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordUserId || !newPassword) return;
    
    try {
      await axios.put(`${API}/users/${passwordUserId}/password`, {
        new_password: newPassword
      }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setPasswordUserId(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const resetForm = () => {
    setUserForm({
      username: '',
      password: '',
      role: 'user',
      is_active: true,
      permissions: {
        dashboard: true,
        stock: true,
        orders: true,
        todo: true,
        pricelist: true,
        accounts: true,
        projects: true,
        analytics: true,
        product_planning: false,
        messaging: false,
        media: false,
        documents: false,
        user_management: false
      }
    });
  };

  const openEditForm = (userData) => {
    setEditingUser(userData);
    setUserForm({
      username: userData.username,
      password: '',
      role: userData.role,
      is_active: userData.is_active,
      permissions: {
        dashboard: true, stock: true, orders: true, todo: true,
        pricelist: true, accounts: true, projects: true, analytics: true,
        product_planning: false, messaging: false, media: false, documents: false,
        user_management: false,
        ...(userData.permissions || {})
      }
    });
    setShowUserForm(true);
  };

  const filteredLogs = useMemo(() => {
    return activityLogs.filter(log => {
      if (logsFilter.searchTerm) {
        const search = logsFilter.searchTerm.toLowerCase();
        return (
          log.username?.toLowerCase().includes(search) ||
          log.ip_address?.toLowerCase().includes(search) ||
          log.city?.toLowerCase().includes(search) ||
          log.country?.toLowerCase().includes(search)
        );
      }
      return true;
    });
  }, [activityLogs, logsFilter.searchTerm]);

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-slate-800">User Management</h2>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setActiveView('users')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'users' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Users
            </button>
            <button
              onClick={() => { setActiveView('logs'); fetchActivityLogs(); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeView === 'logs' ? 'bg-white shadow text-slate-900' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Activity Logs
            </button>
          </div>
          {activeView === 'users' && (
            <Button onClick={() => { resetForm(); setEditingUser(null); setShowUserForm(true); }} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              New User
            </Button>
          )}
        </div>
      </div>

      {/* Users View */}
      {activeView === 'users' && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Username</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Role</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Permissions</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                          {u.username[0].toUpperCase()}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{u.username}</div>
                          {u.is_system && <span className="text-xs text-slate-500">System User</span>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.role === 'admin' ? 'bg-red-100 text-red-700' :
                        u.role === 'srh' ? 'bg-blue-100 text-blue-700' :
                        u.role === 'winlife' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {u.role.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.permissions ? (
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(u.permissions).filter(([k, v]) => v).slice(0, 3).map(([key]) => (
                            <span key={key} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">
                              {key}
                            </span>
                          ))}
                          {Object.values(u.permissions).filter(v => v).length > 3 && (
                            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                              +{Object.values(u.permissions).filter(v => v).length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Full Access</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {/* Show edit button for non-system users OR system users with can_edit_permissions */}
                      {(!u.is_system || u.can_edit_permissions) && (
                        <div className="flex justify-end gap-1">
                          {/* Test as User (impersonation) — admin-only, can't impersonate self */}
                          {user.role === 'admin' && u.username !== user.username && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleImpersonate(u.id, u.username)}
                              className="text-purple-600 border-purple-300 hover:bg-purple-50"
                              title="Test as User — preview the app exactly as they see it"
                              data-testid={`impersonate-${u.username}`}
                            >
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditForm(u)}
                            className="text-blue-600 border-blue-300 hover:bg-blue-50"
                            title={u.is_system ? "Edit Permissions" : "Edit User"}
                          >
                            {u.is_system ? <Shield className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                          </Button>
                          {/* Show password change for SRH, WINLIFE, and non-system users */}
                          {(u.can_edit_permissions || !u.is_system) && u.id !== 'hardcoded-admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setPasswordUserId(u.id); setShowPasswordForm(true); }}
                              className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                              title="Change Password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Delete only for non-system users */}
                          {!u.is_system && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                              title="Delete User"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Activity Logs View */}
      {activeView === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={logsFilter.searchTerm}
                  onChange={(e) => setLogsFilter({ ...logsFilter, searchTerm: e.target.value })}
                  placeholder="Search by username, IP, city..."
                  className="pl-10"
                />
              </div>
              <Button onClick={fetchActivityLogs} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Action</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Device</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">IP Address</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Login Time</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map(log => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                            {log.username[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-800">{log.username}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          log.action === 'login' ? 'bg-green-100 text-green-700' :
                          log.action === 'logout' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-slate-600">
                          {getDeviceIcon(log.device_type)}
                          <div className="text-sm">
                            <div className="capitalize">{log.device_type}</div>
                            {log.browser && <div className="text-xs text-slate-500">{log.browser} / {log.os}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Globe className="w-4 h-4" />
                          {log.city && log.country ? (
                            <span>{log.city}, {log.country}</span>
                          ) : (
                            <span className="text-slate-400">Unknown</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 font-mono">
                        {log.ip_address}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Clock className="w-4 h-4" />
                          {log.login_time ? new Date(log.login_time).toLocaleString('en-IN') : '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDuration(log.session_duration)}
                        {log.logout_time && (
                          <div className="text-xs text-slate-400">
                            Exit: {new Date(log.logout_time).toLocaleTimeString('en-IN')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                        No activity logs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit User Modal */}
      {showUserForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className={`${editingUser?.is_system ? 'bg-gradient-to-r from-purple-600 to-purple-700' : 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white px-6 py-4 rounded-t-xl flex justify-between items-center`}>
              <h3 className="text-lg font-bold">
                {editingUser?.is_system 
                  ? `Edit Permissions - ${editingUser.username}` 
                  : (editingUser ? 'Edit User' : 'Create New User')
                }
              </h3>
              <button onClick={() => { setShowUserForm(false); setEditingUser(null); resetForm(); }} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-80px)]">
              {/* Only show username/password/role for non-system users */}
              {!editingUser?.is_system && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Username *</Label>
                    <Input
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      placeholder="Enter username"
                      required
                      className="mt-1"
                    />
                  </div>
                  
                  {!editingUser && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700">Password *</Label>
                      <div className="relative mt-1">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={userForm.password}
                          onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                          placeholder="Enter password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Role</Label>
                    <select
                      value={userForm.role}
                      onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="user">User</option>
                      <option value="srh">SRH</option>
                      <option value="winlife">WINLIFE</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </>
              )}
              
              {/* System user info banner */}
              {editingUser?.is_system && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Shield className="w-5 h-5" />
                    <span className="font-medium">System User: {editingUser.username}</span>
                  </div>
                  <p className="text-sm text-purple-600 mt-1">
                    You can only modify page/panel permissions for this system user.
                  </p>
                </div>
              )}
              
              <div>
                <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Page/Panel Permissions
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg">
                  {AVAILABLE_PANELS.map(panel => (
                    <label key={panel.key} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded">
                      <input
                        type="checkbox"
                        checked={userForm.permissions[panel.key] || false}
                        onChange={(e) => setUserForm({
                          ...userForm,
                          permissions: { ...userForm.permissions, [panel.key]: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div>
                        <div className="text-sm font-medium text-slate-700">{panel.label}</div>
                        <div className="text-xs text-slate-500">{panel.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Only show Active checkbox for non-system users */}
              {!editingUser?.is_system && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={userForm.is_active}
                    onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Label className="text-sm font-medium text-slate-700">Active User</Label>
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowUserForm(false); setEditingUser(null); resetForm(); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className={`flex-1 ${editingUser?.is_system ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {editingUser?.is_system ? 'Save Permissions' : (editingUser ? 'Update User' : 'Create User')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showPasswordForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold">Change Password</h3>
              <button onClick={() => { setShowPasswordForm(false); setPasswordUserId(null); setNewPassword(''); }} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">New Password *</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowPasswordForm(false); setPasswordUserId(null); setNewPassword(''); }} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-yellow-500 hover:bg-yellow-600">
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
